import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:4000/api'
  : 'https://www.414ministry.org/api';

// Show weeks Sun -> Sat
const DAY_ORDER = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

function Calendar() {
  const [calendar, setCalendar] = useState(null);
  const [error, setError] = useState('');
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [chapterError, setChapterError] = useState('');
  const [readChapters, setReadChapters] = useState(new Set());
  const [selectedVerse, setSelectedVerse] = useState(null);
  const [verseError, setVerseError] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const calendarYear = currentDate.getFullYear();
  const [loading, setLoading] = useState(false);
  
  // arrow availability
  const [canPrev, setCanPrev] = useState(true);
  const [canNext, setCanNext] = useState(true);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Probe helper: return true if the month exists in the API, false otherwise (no UI side effects)
  const monthExists = async (year, month) => {
    const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
    try {
      const res = await axios.get(`${API_URL}/calendar/${monthStr}`, {
        withCredentials: true,
        validateStatus: (s) => s < 500, // don't throw on 4xx so we can evaluate truthiness
      });
      return res.status === 200;
    } catch {
      return false;
    }
  };

  const fetchCalendar = async (year, month) => {
    setLoading(true);
    try {
      const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
      console.log(`Fetching calendar for ${monthStr}...`);
      const calendarResponse = await axios.get(`${API_URL}/calendar/${monthStr}`, { withCredentials: true });
      console.log('API response:', calendarResponse.data);
      const data = typeof calendarResponse.data.json_data === 'string'
        ? JSON.parse(calendarResponse.data.json_data)
        : calendarResponse.data.json_data;

      if (!data || !data.weeks || !Array.isArray(data.weeks)) {
        throw new Error('Invalid calendar format: missing or invalid weeks');
      }

      const daysInMonth = new Date(year, month + 1, 0).getDate();

      // Validate each day Sun..Sat; null out out-of-range daynumbers; keep weeks that have at least one valid day
      const adjustedWeeks = data.weeks
        .map((week) => {
          const newWeek = { ...week };
          DAY_ORDER.forEach((day) => {
            if (
              newWeek[day] &&
              (newWeek[day].daynumber < 1 || newWeek[day].daynumber > daysInMonth)
            ) {
              newWeek[day] = null;
            }
          });
          return newWeek;
        })
        .filter((week) => DAY_ORDER.some((day) => week[day] !== null));

      console.log('Adjusted weeks:', adjustedWeeks);
      setCalendar({ weeks: adjustedWeeks });
      setError('');
      return true;
    } catch (e) {
      console.error('Error fetching calendar:', e);
      setError(`Failed to load calendar for ${monthNames[month]} ${year}: ${e.message}`);
      setCalendar(null);
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const year = currentDate.getFullYear();
  
        // 🔑 reset read state when year changes
        setReadChapters(new Set());
  
        const progressResponse = await axios.get(
          `${API_URL}/progress?year=${year}`,
          { withCredentials: true }
        );
  
        const progressData = progressResponse.data.books || [];
        const readSet = new Set();
  
        progressData.forEach(book => {
          book.chapters_read.forEach(chapter => {
            readSet.add(`${book.book_name} ${chapter}`);
          });
        });
  
        setReadChapters(readSet);
  
        fetchCalendar(year, currentDate.getMonth());
      } catch (e) {
        setError('Failed to load calendar or progress');
      }
    };
  
    fetchData();
  }, [currentDate]); 
  
  // Whenever currentDate changes, probe neighbors to enable/disable arrows
  useEffect(() => {
    (async () => {
      const prev = new Date(currentDate);
      prev.setMonth(prev.getMonth() - 1);
      const next = new Date(currentDate);
      next.setMonth(next.getMonth() + 1);

      const [prevOk, nextOk] = await Promise.all([
        monthExists(prev.getFullYear(), prev.getMonth()),
        monthExists(next.getFullYear(), next.getMonth()),
      ]);

      setCanPrev(prevOk);
      setCanNext(nextOk);
    })();
  }, [currentDate]); // runs after initial load and after any navigation

  const goToMonth = async (year, month) => {
    const ok = await fetchCalendar(year, month);
    if (ok) setCurrentDate(new Date(year, month, 1));
  };

  const handlePrevMonth = async () => {
    const d = new Date(currentDate);
    d.setMonth(d.getMonth() - 1);
    if (await monthExists(d.getFullYear(), d.getMonth())) {
      goToMonth(d.getFullYear(), d.getMonth());
    }
  };

  const handleNextMonth = async () => {
    const d = new Date(currentDate);
    d.setMonth(d.getMonth() + 1);
    if (await monthExists(d.getFullYear(), d.getMonth())) {
      goToMonth(d.getFullYear(), d.getMonth());
    }
  };

  const fetchChapters = async (chapters) => {
    const normalized = Array.isArray(chapters) ? chapters : [chapters];
  
    try {
      const responses = await Promise.all(
        normalized.map(ch =>
          axios.get(`${API_URL}/passage/${encodeURIComponent(ch)}`, {
            withCredentials: true
          })
        )
      );
  
      setSelectedChapter({
        reference: normalized.join(', '),
        chapters: normalized,          // ✅ ALWAYS an array now
        passages: responses.map(r => r.data)
      });
    } catch {
      setChapterError('Failed to load chapters');
    }
  };
  
 

  const fetchVerse = async (verse) => {
    if (!verse) return;

    setVerseError('');
    setSelectedVerse(null);

    try {
      const response = await axios.get(`${API_URL}/passage/${encodeURIComponent(verse)}`, { withCredentials: true });
      setSelectedVerse({
        reference: verse,
        text: response.data,
      });
    } catch (err) {
      console.error('Error fetching verse:', err);
      setVerseError(`Failed to load ${verse}: ${err.response?.data?.error || err.message}`);
    }
  };
  
  const markAsRead = async (chapter) => {
    try {
      console.log('Marking chapter as read:', { chapter });
      const parts = chapter.trim().split(/\s+/);
      if (parts.length < 2) {
        throw new Error('Invalid chapter format. Expected "Book Chapter" (e.g., "1 Corinthians 1")');
      }
      const book = parts.slice(0, -1).join(' ');
      const chapterNum = parseInt(parts[parts.length - 1], 10);
      if (isNaN(chapterNum)) {
        throw new Error(`Invalid chapter number: ${parts[parts.length - 1]}`);
      }

      await axios.post(`${API_URL}/log-progress`, {
        book,
        chapters: [chapterNum],
      }, { withCredentials: true });
      console.log(`Marked ${chapter} as read`);
      setReadChapters(prev => new Set(prev).add(chapter));
      closeChapter();
    } catch (err) {
      console.error('Error marking chapter as read:', err);
      setChapterError('Failed to mark chapter as read: ' + (err.response?.data?.error || err.message));
    }
  };
  const markDayAsRead = async (chapters) => {
    try {
      if (!Array.isArray(chapters) || chapters.length === 0) {
        throw new Error('No chapters provided');
      }
  
      const entries = chapters.map(ch => {
        const parts = ch.trim().split(/\s+/);
        if (parts.length < 2) {
          throw new Error(`Invalid chapter format: ${ch}`);
        }
  
        const book = parts.slice(0, -1).join(' ');
        const chapterNum = parseInt(parts[parts.length - 1], 10);
        if (isNaN(chapterNum)) {
          throw new Error(`Invalid chapter number: ${ch}`);
        }
  
        return { book, chapter: chapterNum };
      });
  
      await axios.post(
        `${API_URL}/log-progress-batch`,
        { entries },
        { withCredentials: true }
      );
  
      setReadChapters(prev => {
        const updated = new Set(prev);
        chapters.forEach(ch => updated.add(ch));
        return updated;
      });
  
      closeChapter();
    } catch (err) {
      setChapterError('Failed to mark day as read');
    }
  };
  
  const closeChapter = () => {
    setSelectedChapter(null);
    setChapterError('');
  };

  const closeVerse = () => {
    setSelectedVerse(null);
    setVerseError('');
  };

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Calendar</h2>
        <p className="text-red-600 mt-2">{error}</p>
      </div>
    );
  }

  if (!calendar || !calendar.weeks) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Calendar</h2>
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  const renderPassage = (data) => {
    console.log("renderPassage input:", data);
    let normalized = data;

    // parse JSON stored as text
    if (typeof data?.text === 'string' && data.text.trim().startsWith('{')) {
      try {
        normalized = JSON.parse(data.text);
      } catch (e) {
        console.error('Failed to parse passage JSON:', e);
        normalized = data;
      }
    }
  
    // ✅ structured format
    if (normalized?.format === 'blocks_v1' && Array.isArray(normalized.blocks)) {
      return normalized.blocks.map((block, index) => {
        if (block.type === 'heading') {
          return (
            <h4 key={index} className="mt-4 mb-2 font-semibold text-gray-900">
              {block.text}
            </h4>
          );
        }
  
        if (block.type === 'verse') {
          return (
            <p key={index} className="leading-relaxed">
              <sup className="mr-1 text-xs text-gray-500">
                {block.verse}
              </sup>
              {block.text}
            </p>
          );
        }
  
        if (block.type === 'verse_continuation') {
          return (
            <p key={index} className="ml-4 leading-relaxed text-gray-700">
              {block.text}
            </p>
          );
        }
  
        return null;
      });
    }
  
    // 🟡 legacy plain text fallback
    if (typeof data?.text === 'string') {
      return data.text.split('\n').map((p, i) => (
        <p key={i}>{p}</p>
      ));
    }
  
    return <p className="text-gray-500">No content available.</p>;
  };
  
  const isMultiChapter =
    Array.isArray(selectedChapter?.chapters) &&
    selectedChapter.chapters.length > 1;
  
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <h2 className="text-xl font-bold text-gray-900 sm:text-2xl mb-1">Calendar</h2>
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={handlePrevMonth}
          className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-50"
          disabled={loading || !canPrev}
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h3 className="text-base font-semibold text-gray-700 sm:text-lg">
          {`${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
        </h3>
        <button
          onClick={handleNextMonth}
          className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-50"
          disabled={loading || !canNext}
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {loading && <p className="text-gray-600">Loading calendar...</p>}
      
      <div className="space-y-4">
        {calendar.weeks.map((week, index) => (
          <div key={index} className="border border-gray-200 rounded-lg shadow-sm p-4 bg-white space-y-3">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
              <h4 className="text-base font-semibold text-gray-900 sm:text-lg">Week {index + 1}</h4>
              {/* {week.memory_verse ? (
                <button
                  onClick={() => fetchVerse(week.memory_verse)}
                  className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  {week.memory_verse} (ESV)
                  <svg className="ml-1 -mr-1 w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ) : (
                <p className="text-xs text-gray-600 sm:text-sm">No Memory Verse</p>
              )} */}
            </div>

            {/* 7-day grid: 2 cols on very small, 4 on small, 7 on very wide */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-3">
              {DAY_ORDER.map((day) => {
                const chapters =week[day]?.chapters ?? (week[day]?.chapter ? [week[day].chapter] : []);              
                const readCount = chapters.filter(ch => readChapters.has(ch)).length;
                const isRead = chapters.length > 0 && readCount === chapters.length;
                const isInProgress = readCount > 0 && readCount < chapters.length;

                return (
                  <button
                    key={day}
                    className={`border border-gray-200 rounded-lg p-3 min-h-[7rem] md:h-28 lg:h-32 flex flex-col transition
                    ${week[day]
                      ? (isRead
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : isInProgress
                          ? 'bg-orange-400 text-white hover:bg-orange-500'
                          : 'bg-gray-50 hover:bg-gray-100')
                      : 'opacity-50 cursor-not-allowed'}
                    
                      hover:shadow`}
                      onClick={() =>
                        week[day] &&
                        fetchChapters(week[day].chapters ?? [week[day].chapter])
                      }
                    disabled={!week[day]}
                    title={day}
                  >
                    {week[day] ? (
                      <>
                        <div className="flex items-center justify-between w-full">
                          <span className={`text-[11px] font-medium ${isRead ? 'text-white/90' : 'text-gray-700'}`}>
                            {day}
                          </span>
                          <span className={`text-[11px] ${isRead ? 'text-white/80' : 'text-gray-500'}`}>
                            {week[day].daynumber}
                          </span>
                        </div>
                        <div className="flex-grow flex items-center justify-center px-1 text-center">
                          <div className="flex flex-col gap-0.5">
                          {chapters.map(ch => {
                            const chRead = readChapters.has(ch);

                            return (
                              <button
                                key={ch}
                                onClick={(e) => {
                                  e.stopPropagation();     // 🔑 don’t trigger day click
                                  fetchChapters([ch]);     // open modal for ONE chapter
                                }}
                                className={`px-2 py-0.5 rounded-full text-xs font-semibold transition
                                  ${chRead
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-200 text-gray-900 hover:bg-gray-300'}
                                `}
                              >
                                {ch}
                              </button>
                            );
                          })}

                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="flex-grow flex items-center justify-center">
                        <p className="text-xs text-gray-400">N/A</p>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 text-sm text-gray-600 text-center">
        Bible passages provided by <a href="https://www.esv.org" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">www.esv.org</a>
      </div>

      {selectedChapter && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">{selectedChapter.reference} (ESV)</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={closeChapter}>
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-grow prose prose-sm max-w-none text-gray-700">
            {selectedChapter.passages.map((passage, i) => (
              <div key={i} className="mb-8">
                {renderPassage(passage)}
              </div>
            ))}

              <p className="mt-4 text-xs text-gray-500">
                Scripture quotations are from the ESV® Bible (The Holy Bible, English Standard Version®), © 2001 by Crossway, a publishing ministry of Good News Publishers. Used by permission. All rights reserved.
              </p>
            </div>
            <div className="p-4 border-t flex justify-end">
            <button
                className="px-4 py-2 bg-blue-600 text-white rounded"
                onClick={() => {
                  if (isMultiChapter) {
                    markDayAsRead(selectedChapter.chapters);
                  } else {
                    markAsRead(selectedChapter.chapters[0]);
                  }
                }}
              >
                Mark as Read
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedVerse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">{selectedVerse.reference} (ESV)</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={closeVerse}>
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-grow prose prose-sm max-w-none text-gray-700">
              {renderPassage(selectedVerse.text)}
              <p className="mt-4 text-xs text-gray-500">
                Scripture quotations are from the ESV® Bible (The Holy Bible, English Standard Version®), © 2001 by Crossway, a publishing ministry of Good News Publishers. Used by permission. All rights reserved.
              </p>
            </div>
            <div className="p-4 border-t flex justify-end">
              <button
                onClick={closeVerse}
                className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700 transition-colors duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {chapterError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Error</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={closeChapter}>
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-red-700">{chapterError}</p>
          </div>
        </div>
      )}

      {verseError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Error</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={closeVerse}>
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-red-700">{verseError}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Calendar;
